import { Extension } from '@tiptap/vue-3'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { EditorState, Transaction } from '@tiptap/pm/state'
import type { Mark, MarkType, Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

import { findLiquidSpans } from '../liquid'

// Doc-position ranges of every Liquid span in the document. Each maximal run of
// consecutive text nodes is scanned on its own. Scanning across sibling text
// nodes matters because formatting splits Liquid — bolding inside
// `{{ profit_loss }}` leaves `{{ `, `profit_loss` (bold) and ` }}` as separate
// (but adjacent) text nodes, so no single node holds the whole span. A non-text
// inline node (mention chip, hard break) ends the run, so a Liquid span cannot
// straddle one. Adjacent text nodes are contiguous in the document, so a run's
// doc positions are just runStart + offset-within-run.
const liquidRanges = (doc: ProseMirrorNode): { from: number; to: number }[] => {
  const ranges: { from: number; to: number }[] = []

  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true

    let runText = ''
    let runStart = 0

    const flushRun = (): void => {
      if (!runText) return
      findLiquidSpans(runText).forEach(({ start, end }) => {
        ranges.push({ from: runStart + start, to: runStart + end })
      })
      runText = ''
    }

    node.forEach((child, offset) => {
      if (child.isText && child.text) {
        if (!runText) runStart = pos + 1 + offset
        runText += child.text
      } else {
        flushRun()
      }
    })
    flushRun()

    return false // inline content already handled above
  })

  return ranges
}

// Mark types that cover only part of the [from, to) range. A mark type is
// left alone only when one identical mark — same type AND attributes — sits on
// every text node of the span (e.g. `**{{ x }}**`), because that serializes
// cleanly. Comparing attributes matters: two adjacent links with different
// hrefs share the `link` type but neither covers the whole span, so they must
// both be stripped rather than mistaken for a single wrapping link.
const partialMarkTypes = (
  doc: ProseMirrorNode,
  from: number,
  to: number
): MarkType[] => {
  const present = new Map<string, MarkType>()
  let common: Mark[] | null = null

  doc.nodesBetween(from, to, (node) => {
    if (!node.isText) return
    node.marks.forEach((mark) => present.set(mark.type.name, mark.type))
    common =
      common === null
        ? [...node.marks]
        : common.filter((mark) => node.marks.some((other) => other.eq(mark)))
  })

  const covering: Mark[] = common ?? []
  const fullyCovering = new Set(covering.map((mark) => mark.type.name))
  return [...present.entries()]
    .filter(([name]) => !fullyCovering.has(name))
    .map(([, type]) => type)
}

// Highlights Liquid ({{ output }} / {% tags %}) spans inline, without turning
// them into nodes. The template source stays ordinary, freely-editable text —
// the user can place the cursor inside it and type — while reading visually
// apart from prose. The markdown round-trip leaves the source untouched via the
// Liquid-aware turndown escape (see ../liquid.ts); this extension styles the
// spans and strips any inline formatting from them.
export const LiquidDecoration = Extension.create({
  name: 'liquidDecoration',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('liquidDecoration'),
        // A mark that covers only part of a Liquid expression (bold/italic/link
        // on the body but not the delimiters, or a styled paste) splits it
        // across text nodes, so the serializer injects Markdown or escapes
        // characters inside the template — `{{ **profit\_loss** }}` — which
        // Liquid then cannot parse (it renders empty). Strip those. A mark that
        // covers the whole span (e.g. `**{{ profit_loss }}**`) styles the
        // rendered output and serializes cleanly, so keep it.
        appendTransaction: (
          transactions: readonly Transaction[],
          _oldState: EditorState,
          newState: EditorState
        ): Transaction | null => {
          if (!transactions.some((tr) => tr.docChanged)) return null

          const { tr } = newState
          liquidRanges(newState.doc).forEach(({ from, to }) => {
            partialMarkTypes(newState.doc, from, to).forEach((type) => {
              tr.removeMark(from, to, type)
            })
          })

          return tr.steps.length ? tr : null
        },
        props: {
          decorations: (state: EditorState): DecorationSet => {
            const decorations: Decoration[] = []

            liquidRanges(state.doc).forEach(({ from, to }) => {
              decorations.push(Decoration.inline(from, to, { class: 'liquid' }))
            })

            return DecorationSet.create(state.doc, decorations)
          },
        },
      }),
    ]
  },
})

export default LiquidDecoration
